import React, { useState, useEffect } from "react";
import { isEmpty } from "lodash";

import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Spinner,
  Badge,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import toast from "react-hot-toast";
// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

//redux
import { useSelector, useDispatch } from "react-redux";

import avatar from "../../assets/images/users/avatar-1.jpg";
// actions
import { editProfile, resetProfileFlag } from "../../slices/thunks";
import { createSelector } from "reselect";
import { getLoggedInUser } from "../../helpers/fakebackend_helper";
import UserService from "../../services/OrderingServices/ProfileServices";
import StatusBadge from "../../Components/Common/Badge";
// import UserService from "../../services/UserService";

const UserProfile = () => {
  const dispatch = useDispatch();

  const auth = getLoggedInUser();

  // State for organization data
  const [organizationData, setOrganizationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  //Logo
  const [logoModal, setLogoModal] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [orgModal, setOrgModal] = useState(false);

  // const [sectionUpdating, setSectionUpdating] = useState(false);
  //Take Away
  const [usesSection, setUsesSection] = useState(false);

  const selectLayoutState = (state) => state.Profile;
  const userprofileData = createSelector(selectLayoutState, (state) => ({
    user: state.user,
    success: state.success,
    error: state.error,
  }));

  const { user, success, error } = useSelector(userprofileData);

  const orgFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: organizationData?.name || "",
      orgCode: organizationData?.orgCode || "",
      address1: organizationData?.address1 || "",
      tel: organizationData?.tel || "",
      contactNo: organizationData?.contactNo || "",
      email: organizationData?.email || "",
      adminEmail: organizationData?.adminEmail || "",
      panNumber: organizationData?.panNumber || "",
      isActive: organizationData?.isActive || true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Organization Name is required"),
      orgCode: Yup.string().required("Organization Code is required"),
      address1: Yup.string().required("Address is required"),
      tel: Yup.string().required("Contact is required"),
      email: Yup.string().email("Invalid email").required("Email required"),
      adminEmail: Yup.string()
        .email("Invalid email")
        .required("Admin Email required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await UserService.updateOrganization(organizationData.id, values);
        toast.success("Organization updated successfully!");
        setOrgModal(false);
        await fetchOrganizationData();
      } catch (err) {
        toast.error("Failed to update organization");
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      const data = await UserService.getList();
      setOrganizationData(data);

      // set initial toggle states
      // setUsesKotBot(data.organizationInfo?.usesKotBotService || false);
      // setUsesTakeAway(data.organizationInfo?.enableTakeAway || false);
      // setUsesSection(data.organizationInfo?.shouldShowSections || false);

      setFetchError(null);
    } catch (err) {
      console.error("Error fetching organization data:", err);
      setFetchError("Failed to load organization data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: organizationData?.name || auth.firstName || "Admin",
      id: organizationData?.id || auth.id || "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please Enter Organization Name"),
    }),
    onSubmit: (values) => {
      dispatch(editProfile(values));
    },
  });

  document.title = "Ordering System | Profile";

  // Get logo URL
  const getLogoUrl = () => {
    if (organizationData?.organizationInfo?.logoPath) {
      return `https://api-waitermoduleapp.danfesolution.com/${
        organizationData.organizationInfo.logoPath
      }?t=${new Date().getTime()}`;
    }
    return avatar;
  };

  const InfoRow = ({ label, value, valueClass = "" }) => (
    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
      <span className="text-muted" style={{ fontSize: "14px" }}>
        {label}:
      </span>
      <span className={`fw-medium ${valueClass}`} style={{ fontSize: "14px" }}>
        {value}
      </span>
    </div>
  );

  const handleUpdateOrganizationName = async () => {
    if (!validation.values.firstName) {
      toast.error("Organization name cannot be empty");
      return;
    }

    try {
      setLoading(true);

      // Prepare payload for updateOrganization API
      const payload = {
        name: validation.values.firstName,
        orgCode: organizationData?.orgCode || "",
        address1: organizationData?.address1 || "",
        tel: organizationData?.tel || "",
        contactNo: organizationData?.contactNo || "",
        email: organizationData?.email || "",
        adminEmail: organizationData?.adminEmail || "",
        panNumber: organizationData?.panNumber || "",
        isActive: organizationData?.isActive || true,
      };

      await UserService.updateOrganization(organizationData.id, payload);

      toast.success("Organization name updated successfully!");

      // Refresh organization data to show updated name
      const updatedData = await UserService.getList();
      setOrganizationData(updatedData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update organization name");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content" style={{ backgroundColor: "#f8f9fa" }}>
        <Container fluid>
          {/* Alerts */}
          {error && (
            <Alert color="danger" className="mb-4">
              {error}
            </Alert>
          )}
          {fetchError && (
            <Alert color="danger" className="mb-4">
              {fetchError}
            </Alert>
          )}
          {success && (
            <Alert color="success" className="mb-4">
              Profile Updated Successfully!
            </Alert>
          )}

          {loading ? (
            <Card className="shadow-sm border-0">
              <CardBody className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Loading organization data...</p>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* Main Profile Card */}
              <Card className="shadow-sm border-0 mb-4">
                <CardBody className="p-4">
                  <Row>
                    <Col lg={4} className="text-center mb-4 mb-lg-0">
                      <div className="position-relative d-inline-block">
                        {/* LOGO IMAGE */}
                        <img
                          src={getLogoUrl()}
                          alt="Organization Logo"
                          className="rounded-circle"
                          style={{
                            width: "150px",
                            height: "150px",
                            objectFit: "cover",
                            border: "4px solid #f0f0f0",
                          }}
                          onError={(e) => {
                            e.target.src = avatar;
                          }}
                        />

                        {/* EDIT BUTTON */}
                        <button
                          className="btn btn-light rounded-circle shadow position-absolute d-flex align-items-center justify-content-center"
                          style={{
                            top: "5px",
                            right: "5px",
                            width: "34px",
                            height: "34px",
                            padding: "0",
                          }}
                          onClick={() => setLogoModal(true)}
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                      </div>

                      <h4 className="mt-3 mb-1">
                        {organizationData?.name || "Organization"}
                      </h4>
                    </Col>

                    <Col lg={8}>
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                        <h5 className="mb-0">Organization Information</h5>

                        <div className="d-flex align-items-center gap-2">
                          <StatusBadge
                            status={organizationData?.isActive}
                            type="status"
                            pill={true}
                          />

                          <Button
                            color="light"
                            className="btn-sm"
                            onClick={() => setOrgModal(true)}
                          >
                            <i className="ri-edit-line"></i>
                          </Button>
                        </div>
                      </div>

                      <Row>
                        <Col md={6}>
                          <InfoRow
                            label="Organization Code"
                            value={organizationData?.orgCode || "N/A"}
                          />
                          <InfoRow
                            label="Email"
                            value={organizationData?.email || "N/A"}
                          />
                          <InfoRow
                            label="Admin Email"
                            value={organizationData?.adminEmail || "N/A"}
                          />
                        </Col>
                        <Col md={6}>
                          <InfoRow
                            label="Contact"
                            value={organizationData?.tel || "N/A"}
                          />
                          <InfoRow
                            label="Address"
                            value={organizationData?.address1 || "N/A"}
                          />
                          {organizationData?.address2 && (
                            <InfoRow
                              label="Address 2"
                              value={organizationData.address2}
                            />
                          )}
                        </Col>
                      </Row>

                      {organizationData?.panNumber && (
                        <Row className="mt-2">
                          <Col>
                            <InfoRow
                              label="PAN Number"
                              value={organizationData.panNumber}
                            />
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Organization Details Card */}
              {/* Organization Timeline Card */}
              {organizationData?.organizationInfo && (
                <Card className="shadow-sm border-0 mb-4">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "rgba(102, 126, 234, 0.1)",
                          color: "#667eea",
                        }}
                      >
                        <i className="ri-calendar-line fs-5"></i>
                      </div>
                      <h5 className="mb-0 fw-semibold">Organization Details</h5>
                    </div>

                    <Row className="g-4">
                      <Col md={4}>
                        <div
                          className="text-center p-3 rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <i className="ri-login-circle-line fs-2 text-success mb-2"></i>
                          <p
                            className="text-muted mb-1"
                            style={{ fontSize: "13px" }}
                          >
                            Joined Date
                          </p>
                          <h6 className="mb-0 fw-semibold">
                            {new Date(
                              organizationData.organizationInfo.joinedDate
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </h6>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div
                          className="text-center p-3 rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <i className="ri-logout-circle-line fs-2 text-danger mb-2"></i>
                          <p
                            className="text-muted mb-1"
                            style={{ fontSize: "13px" }}
                          >
                            Expiry Date
                          </p>
                          <h6 className="mb-0 fw-semibold">
                            {new Date(
                              organizationData.organizationInfo.expiryDate
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </h6>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div
                          className="text-center p-3 rounded"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          <i
                            className={`ri-${
                              organizationData.organizationInfo.isDemoUser
                                ? "flask"
                                : "shield-check"
                            }-line fs-2 ${
                              organizationData.organizationInfo.isDemoUser
                                ? "text-warning"
                                : "text-primary"
                            } mb-2`}
                          ></i>
                          <p
                            className="text-muted mb-1"
                            style={{ fontSize: "13px" }}
                          >
                            Account Type
                          </p>
                          <h6 className="mb-0 fw-semibold">
                            {organizationData.organizationInfo.isDemoUser
                              ? "Demo User"
                              : "Full Access"}
                          </h6>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}

              {/* Update Organization Name Card */}
              <Card className="shadow-sm border-0">
                <CardBody className="p-4">
                  <h5 className="mb-4">Update Organization Name</h5>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdateOrganizationName();
                    }}
                  >
                    <Row>
                      <Col md={8}>
                        <div className="mb-3">
                          <Label className="form-label fw-medium">
                            Organization Name
                          </Label>
                          <Input
                            name="firstName"
                            className="form-control"
                            placeholder="Enter Organization Name"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.firstName || ""}
                            invalid={
                              validation.touched.firstName &&
                              validation.errors.firstName
                                ? true
                                : false
                            }
                            disabled={loading}
                            style={{ height: "45px" }}
                          />
                          {validation.touched.firstName &&
                          validation.errors.firstName ? (
                            <FormFeedback type="invalid">
                              {validation.errors.firstName}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col md={4} className="d-flex align-items-end">
                        <Button
                          type="submit"
                          color="primary"
                          disabled={loading}
                          className="w-100 mb-3"
                          style={{ height: "45px" }}
                        >
                          {loading ? (
                            <Spinner size="sm" />
                          ) : (
                            "Update Organization Name"
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>

      <Modal isOpen={logoModal} toggle={() => setLogoModal(false)}>
        <ModalHeader toggle={() => setLogoModal(false)}>
          Update Organization Logo
        </ModalHeader>

        <ModalBody>
          <div className="mb-3">
            <Label className="fw-bold">Choose Image</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files[0])}
            />
          </div>

          <div className="d-flex justify-content-end">
            <Button
              color="secondary"
              className="me-2"
              onClick={() => setLogoModal(false)}
            >
              Cancel
            </Button>

            <Button
              color="primary"
              disabled={!logoFile || uploadingLogo}
              onClick={async () => {
                try {
                  setUploadingLogo(true);
                  await UserService.updateLogo(organizationData.id, logoFile);
                  toast.success("Logo updated successfully!");

                  setLogoModal(false);
                  setLogoFile(null);

                  await fetchOrganizationData();
                } catch (err) {
                  toast.error("Failed to upload logo");
                } finally {
                  setUploadingLogo(false);
                }
              }}
            >
              {uploadingLogo ? <Spinner size="sm" /> : "Upload"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={orgModal} toggle={() => setOrgModal(false)}>
        <ModalHeader toggle={() => setOrgModal(false)}>
          Edit Organization Details
        </ModalHeader>
        <ModalBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              orgFormik.handleSubmit();
            }}
          >
            <Row>
              <Col md={6} className="mb-3">
                <Label>Name</Label>
                <Input
                  name="name"
                  value={orgFormik.values.name}
                  onChange={orgFormik.handleChange}
                  onBlur={orgFormik.handleBlur}
                  invalid={orgFormik.touched.name && orgFormik.errors.name}
                />
                {orgFormik.touched.name && orgFormik.errors.name && (
                  <FormFeedback>{orgFormik.errors.name}</FormFeedback>
                )}
              </Col>
              <Col md={6} className="mb-3">
                <Label>Organization Code</Label>
                <Input
                  name="orgCode"
                  value={orgFormik.values.orgCode}
                  onChange={orgFormik.handleChange}
                  onBlur={orgFormik.handleBlur}
                  invalid={
                    orgFormik.touched.orgCode && orgFormik.errors.orgCode
                  }
                />
                {orgFormik.touched.orgCode && orgFormik.errors.orgCode && (
                  <FormFeedback>{orgFormik.errors.orgCode}</FormFeedback>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Label>Address</Label>
                <Input
                  name="address1"
                  value={orgFormik.values.address1}
                  onChange={orgFormik.handleChange}
                  onBlur={orgFormik.handleBlur}
                  invalid={
                    orgFormik.touched.address1 && orgFormik.errors.address1
                  }
                />
                {orgFormik.touched.address1 && orgFormik.errors.address1 && (
                  <FormFeedback>{orgFormik.errors.address1}</FormFeedback>
                )}
              </Col>
              <Col md={6} className="mb-3">
                <Label>Contact</Label>
                <Input
                  name="tel"
                  value={orgFormik.values.tel}
                  onChange={orgFormik.handleChange}
                  onBlur={orgFormik.handleBlur}
                  invalid={orgFormik.touched.tel && orgFormik.errors.tel}
                />
                {orgFormik.touched.tel && orgFormik.errors.tel && (
                  <FormFeedback>{orgFormik.errors.tel}</FormFeedback>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Label>Email</Label>
                <Input
                  name="email"
                  type="email"
                  value={orgFormik.values.email}
                  onChange={orgFormik.handleChange}
                  onBlur={orgFormik.handleBlur}
                  invalid={orgFormik.touched.email && orgFormik.errors.email}
                />
                {orgFormik.touched.email && orgFormik.errors.email && (
                  <FormFeedback>{orgFormik.errors.email}</FormFeedback>
                )}
              </Col>
              <Col md={6} className="mb-3">
                <Label>Admin Email</Label>
                <Input
                  name="adminEmail"
                  type="email"
                  value={orgFormik.values.adminEmail}
                  onChange={orgFormik.handleChange}
                  onBlur={orgFormik.handleBlur}
                  invalid={
                    orgFormik.touched.adminEmail && orgFormik.errors.adminEmail
                  }
                />
                {orgFormik.touched.adminEmail &&
                  orgFormik.errors.adminEmail && (
                    <FormFeedback>{orgFormik.errors.adminEmail}</FormFeedback>
                  )}
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Label>PAN Number</Label>
                <Input
                  name="panNumber"
                  value={orgFormik.values.panNumber}
                  onChange={orgFormik.handleChange}
                />
              </Col>
              <Col md={6} className="mb-3">
                <FormGroup check>
                  <Input
                    type="checkbox"
                    name="isActive"
                    checked={orgFormik.values.isActive}
                    onChange={(e) =>
                      orgFormik.setFieldValue("isActive", e.target.checked)
                    }
                  />
                  <Label check>Active</Label>
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button
                color="secondary"
                className="me-2"
                onClick={() => setOrgModal(false)}
              >
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Update
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default UserProfile;
